import React, { useEffect, useRef, useState } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

/**
 * Inline gate shown to logged-in users who haven't verified their email yet.
 * Renders a friendly "check your inbox" screen with a Resend button and
 * polls `/api/auth/verification-status` every 5s so the moment the user
 * taps the email link on another tab / device, the gate auto-dismisses.
 *
 * Callers render this INSTEAD of the trade UI when `user.email_verified` is
 * false. It doesn't full-page block the app; the user can still navigate
 * to Wallet, Home, etc. It's only shown at the trade entry point.
 */
export default function VerifyEmailGate({ context = 'trade' }) {
  const { user, refreshMe } = useAuth();
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  // Poll every 5s so an out-of-band verify (email link on another tab)
  // dismisses this gate quickly.
  useEffect(() => {
    if (!user || user.email_verified) return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get('/auth/verification-status');
        if (data.email_verified) {
          clearInterval(pollRef.current);
          await refreshMe();
        }
      } catch { /* keep polling */ }
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [user, refreshMe]);

  const handleResend = async () => {
    setResending(true);
    setError(null);
    try {
      await api.post('/auth/resend-verification');
      setResent(true);
      setTimeout(() => setResent(false), 6000);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not resend right now — try again in a minute.');
    } finally {
      setResending(false);
    }
  };

  const contextCopy = context === 'trade'
    ? 'To keep bots off Clanster, every trader verifies their email once. Real users only see this screen once.'
    : 'Verify your email to unlock the rest of Clanster.';

  return (
    <div className="verify-gate" data-testid="verify-email-gate">
      <div className="verify-gate-hero" aria-hidden="true">📮</div>
      <h2 className="verify-gate-title">Check your inbox</h2>
      <p className="verify-gate-lead">
        We sent a verification link to<br />
        <b data-testid="verify-gate-email">{user?.email || 'your email'}</b>
      </p>
      <p className="verify-gate-sub">{contextCopy}</p>

      <button
        className="verify-gate-cta"
        onClick={handleResend}
        disabled={resending || resent}
        data-testid="verify-resend-btn"
      >
        {resent ? '✓ Sent — check your inbox' : (resending ? 'Sending…' : '↻ Resend verification email')}
      </button>

      {error && (
        <div className="verify-gate-error" data-testid="verify-gate-error">{error}</div>
      )}

      <p className="verify-gate-foot">
        We'll unlock trading automatically the moment you tap the link — no need to refresh.
      </p>
    </div>
  );
}
